<template>
  <div
    class="max-h-[min(40vh,28rem)] min-h-0 overflow-y-auto overscroll-contain"
    data-testid="uod-branch-context-list"
    @scroll="onScroll"
  >
    <div
      v-for="item in table"
      :key="item.id"
      class="flex min-h-12 cursor-pointer items-center gap-3 border-b border-th-border-lighter py-2.5 last:border-b-0"
      :class="{
        'cursor-not-allowed opacity-[0.45]': item.disabled
      }"
      data-testid="uod-branch-context-item"
      @click="toggleItem(item)"
    >
      <el-checkbox
        :model-value="isSelected(item)"
        :disabled="item.disabled"
        class="h-auto shrink-0 [&_.el-checkbox__label]:hidden"
        @click.stop
        @update:model-value="(checked) => setItemSelected(item, checked)"
      />
      <span
        class="min-w-0 flex-1 text-left text-base font-bold leading-5 tracking-[0.56px] text-th-text-primary"
      >
        {{ item.label }}
      </span>
      <div class="ml-auto flex shrink-0 items-center justify-end gap-2">
        <span
          v-if="item.mms_unit_unzer_id"
          class="inline-flex items-center whitespace-nowrap rounded border border-th-border-light bg-th-fill-light px-2 py-0.5 text-xs font-normal leading-4 tracking-[0.6px] text-th-text-primary"
        >
          {{ item.mms_unit_unzer_id }}
        </span>
        <span
          v-for="type in getBranchTypes(item.mms_type)"
          :key="type"
          class="inline-flex items-center whitespace-nowrap rounded border border-th-border-light bg-th-fill-light px-2 py-0.5 text-xs font-normal leading-4 tracking-[0.6px] text-th-text-primary"
        >
          {{ type }}
        </span>
      </div>
    </div>
    <div
      v-if="!table.length"
      class="py-6 text-center text-base text-th-text-secondary"
    >
      {{ $t('common.data.no_results') }}
    </div>
  </div>
</template>

<script>
import debounce from 'debounce'
import { allBranches, useLocationStore } from '@shared/store/location'
import { getBranchTypes } from '@shared/constants/branch-type'

export default {
  props: {
    table: {
      type: Array,
      default: () => []
    },
    infiniteScroll: {
      type: Boolean,
      default: false
    },
    canSelectAllBranches: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'paginate', 'selection-change'],
  setup() {
    const locationStore = useLocationStore()
    return { locationStore }
  },
  data() {
    return {
      selectedIds: [],
      allSelected: false,
      hasUserModifiedSelection: false
    }
  },
  computed: {
    selectableItems() {
      return this.table.filter((item) => !item.disabled)
    },
    isAllSelected() {
      return this.allSelected
    },
    canApplySelection() {
      if (this.allSelected && this.canSelectAllBranches) return true
      return this.selectedIds.length === 1
    }
  },
  watch: {
    table: {
      immediate: true,
      handler() {
        if (!this.hasUserModifiedSelection) {
          this.syncSelectionFromStore()
        }
      }
    },
    selectedIds: {
      deep: true,
      handler() {
        this.emitSelectionChange()
      }
    },
    allSelected() {
      this.emitSelectionChange()
    },
    canSelectAllBranches() {
      this.emitSelectionChange()
    },
    isSearching: {
      immediate: true,
      handler(active) {
        // AC: while a search filter is active the results must show nothing
        // selected. Clear any inherited selection so the user starts from a
        // clean slate; a branch picked during the search still persists after.
        if (active) this.clearSelectionForSearch()
      }
    }
  },
  methods: {
    getBranchTypes,
    isSelected(item) {
      if (item.disabled) return false
      return this.allSelected || this.selectedIds.includes(item.id)
    },
    emitSelectionChange() {
      this.$emit('selection-change', this.canApplySelection)
    },
    resetUserSelectionState() {
      this.hasUserModifiedSelection = false
    },
    clearSelectionForSearch() {
      // Mark modified so the filtered `table` fetch doesn't re-sync a selection
      // from the store; the search view stays empty until the user picks one.
      this.markSelectionModified()
      this.allSelected = false
      this.selectedIds = []
    },
    markSelectionModified() {
      this.hasUserModifiedSelection = true
    },
    syncSelectionFromStore() {
      const enabledIds = this.selectableItems.map((item) => item.id)
      const { currentLocation, locationContext } = this.locationStore
      const isAllBranchesContext =
        !currentLocation &&
        locationContext?.branches?.[0]?.label === allBranches.label

      if (currentLocation && enabledIds.includes(currentLocation)) {
        this.allSelected = false
        this.selectedIds = [currentLocation]
        return
      }

      if (isAllBranchesContext && this.canSelectAllBranches) {
        this.allSelected = true
        this.selectedIds = []
        return
      }

      const contextIds = (locationContext?.branches || [])
        .map((branch) => branch.id)
        .filter((id) => id && enabledIds.includes(id))

      this.allSelected = false
      this.selectedIds = contextIds.length === 1 ? contextIds : []
    },
    selectAll() {
      if (!this.canSelectAllBranches) return
      this.markSelectionModified()
      // "All" is a mode, not an enumeration of the loaded page — otherwise
      // branches on later (not-yet-fetched) pages would be left unselected.
      this.allSelected = true
      this.selectedIds = []
    },
    clearAll() {
      this.markSelectionModified()
      this.allSelected = false
      this.selectedIds = []
    },
    selectSingleItem(item) {
      this.markSelectionModified()
      this.allSelected = false
      this.selectedIds = [item.id]
    },
    toggleItem(item) {
      if (item.disabled) return

      if (this.isAllSelected) {
        this.selectSingleItem(item)
        return
      }

      if (this.isSelected(item)) {
        this.selectedIds = []
      } else {
        this.selectSingleItem(item)
      }
    },
    setItemSelected(item, checked) {
      if (item.disabled) return

      if (this.isAllSelected) {
        this.selectSingleItem(item)
        return
      }

      if (checked) {
        this.selectSingleItem(item)
      } else {
        this.selectedIds = []
      }
    },
    applySelection() {
      if (!this.canApplySelection) return

      if (this.selectedIds.length === 1) {
        const branch = this.table.find(
          (item) => item.id === this.selectedIds[0]
        )
        if (!branch) return

        this.locationStore.setLocationContext({
          branches: [branch],
          location: branch.id
        })
      } else if (this.isAllSelected) {
        this.locationStore.$reset()
      }

      this.$nextTick(() => {
        this.$emitter.emit('refresh-requested')
        this.$emit('close')
      })
    },
    onScroll: debounce(function (event) {
      if (!this.infiniteScroll) return

      const { scrollTop, offsetHeight, scrollHeight } = event.target
      if (scrollTop + offsetHeight >= scrollHeight) {
        this.$emit('paginate')
      }
    }, 100)
  }
}
</script>
